import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Typography, Chip } from '@mui/material';
import { HourglassEmpty as HourglassIcon, Person as PersonIcon } from '@mui/icons-material';
import { getCurrencySymbol } from '../../utils/currency';

// Formatage universel des dates: DD/MM/YYYY HH:MM
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Banner pour l'admin/owner montrant qu'une validation 3DS est en attente
 * côté renter suite à une décision administrative
 */
function AdminPaymentAuthAdminBanner({ authRequest, isAdmin = false, disputeStatus }) {
  const { t } = useTranslation('disputes');
  if (!authRequest) {
    return null;
  }

  const status = authRequest.status;

  const getBannerConfig = () => {
    switch (status) {
      case 'pending_auth':
        return {
          severity: 'info',
          title: t('adminPaymentAuth.adminBanners.pendingAuth.title'),
          statusLabel: t('adminPaymentAuth.adminBanners.pendingAuth.statusLabel')
        };
      case 'authenticated':
        return {
          severity: 'info',
          title: t('adminPaymentAuth.adminBanners.authenticated.title'),
          statusLabel: t('adminPaymentAuth.adminBanners.authenticated.statusLabel')
        };
      case 'completed':
        return {
          severity: 'success',
          title: t('adminPaymentAuth.adminBanners.completed.title'),
          statusLabel: t('adminPaymentAuth.adminBanners.completed.statusLabel')
        };
      case 'cancelled':
        return {
          severity: 'warning',
          title: t('adminPaymentAuth.adminBanners.cancelled.title'),
          statusLabel: t('adminPaymentAuth.adminBanners.cancelled.statusLabel')
        };
      case 'expired':
        return {
          severity: 'warning',
          title: t('adminPaymentAuth.adminBanners.expired.title'),
          statusLabel: t('adminPaymentAuth.adminBanners.expired.statusLabel')
        };
      case 'failed':
        return {
          severity: 'error',
          title: t('adminPaymentAuth.adminBanners.failed.title'),
          statusLabel: t('adminPaymentAuth.adminBanners.failed.statusLabel')
        };
      default:
        return {
          severity: 'info',
          title: t('adminPaymentAuth.adminBanners.default.title'),
          statusLabel: status
        };
    }
  };

  const banner = getBannerConfig();

  const borderColor =
    banner.severity === 'success' ? 'success.main' :
    banner.severity === 'error' ? 'error.main' :
    banner.severity === 'warning' ? 'warning.main' :
    'info.main';

  const getHintText = () => {
    switch (status) {
      case 'pending_auth':
        return t('adminPaymentAuth.adminBanners.pendingAuth.hint');
      case 'authenticated':
        return t('adminPaymentAuth.adminBanners.authenticated.hint');
      case 'completed':
        return t('adminPaymentAuth.adminBanners.completed.hint');
      case 'cancelled':
        return t('adminPaymentAuth.adminBanners.cancelled.hint');
      case 'expired':
        return t('adminPaymentAuth.adminBanners.expired.hint');
      case 'failed':
        return authRequest.failure_reason
          ? t('adminPaymentAuth.adminBanners.failed.hintWithReason', { reason: authRequest.failure_reason })
          : t('adminPaymentAuth.adminBanners.failed.hint');
      default:
        return t('adminPaymentAuth.adminBanners.default.hint');
    }
  };

  // Symbole de devise
  const currencySymbol = getCurrencySymbol(authRequest.currency || 'EUR');

  return (
    <Alert
      severity={banner.severity}
      icon={<HourglassIcon fontSize="large" />}
      sx={{
        mb: 3,
        border: '2px solid',
        borderColor,
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
    >
      <AlertTitle sx={{ fontWeight: 'bold', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
        <HourglassIcon />
        {banner.title}
        <Chip 
          label={isAdmin ? t('adminPaymentAuth.adminBanners.labels.admin') : t('adminPaymentAuth.adminBanners.labels.owner')} 
          size="small" 
          color={isAdmin ? "primary" : "success"} 
        />
      </AlertTitle>
      
      <Box sx={{ mt: 1 }}>
        <Typography variant="body1" gutterBottom>
          {isAdmin ? (
            <>{t('adminPaymentAuth.adminBanners.labels.updateFromAdmin')}</>
          ) : (
            <>{t('adminPaymentAuth.adminBanners.labels.updateFromOwner')}</>
          )}{' '}
          {t('adminPaymentAuth.labels.amount')}{' '}
          <strong style={{ fontSize: '1.2rem', color: 'info.main' }}>
            {authRequest.amount} {currencySymbol}
          </strong>
        </Typography>

        <Box 
          sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'background.paper', 
            borderRadius: 1,
            borderLeft: '4px solid',
            borderColor
          }}
        >
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <strong>{t('adminPaymentAuth.adminBanners.labels.renterConcerned')}</strong>
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            <strong>{t('adminPaymentAuth.labels.status')}:</strong> {banner.statusLabel}
          </Typography>
          
          {authRequest.expires_at && disputeStatus !== 'resolved' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ⏰ {t('adminPaymentAuth.adminBanners.labels.expiresAt')}{' '}
              {formatDate(authRequest.expires_at)}
            </Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}>
          {getHintText()}
        </Typography>
      </Box>
    </Alert>
  );
}

export default AdminPaymentAuthAdminBanner;
